import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { fmtPubDate } from "./OltreLeCarte";
import { ArrowLeft, Feather, Heart } from "lucide-react";
import { toast } from "sonner";

const LIKED_KEY = "arcana-liked-posts";

const ytId = (url) => {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : null;
};

export default function OltreLeCartePost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    api.get(`/posts/${id}`).then((r) => {
      setPost(r.data);
      setLikes(r.data.likes ?? 0);
    }).catch(() => setError(true));
    try {
      setLiked(JSON.parse(localStorage.getItem(LIKED_KEY) || "[]").includes(id));
    } catch {}
  }, [id]);

  const like = async () => {
    if (liked) return;
    setLiked(true);
    setLikes((n) => n + 1);
    try {
      const r = await api.post(`/posts/${id}/like`);
      setLikes(r.data.likes);
      const arr = JSON.parse(localStorage.getItem(LIKED_KEY) || "[]");
      localStorage.setItem(LIKED_KEY, JSON.stringify([...arr, id]));
    } catch {
      setLiked(false);
      setLikes((n) => Math.max(0, n - 1));
      toast.error("Non riesco a registrare il tuo Mi piace. Riprova.");
    }
  };

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="oltre-post-error">
        <p className="text-purple-200/70">Contenuto non trovato.</p>
        <Link to="/oltre-le-carte" className="text-amber-300 text-sm mt-4 inline-block">← Torna a Oltre le Carte</Link>
      </div>
    );
  }
  if (!post) {
    return <p className="text-center text-purple-300/50 text-sm py-24" data-testid="oltre-post-loading">Caricamento…</p>;
  }

  const media = post.media || [];

  return (
    <article className="max-w-3xl mx-auto px-4 lg:px-8 py-14 sm:py-20" data-testid="oltre-post-page">
      <Link to="/oltre-le-carte" data-testid="oltre-back-link" className="inline-flex items-center gap-2 text-xs text-purple-300/60 hover:text-amber-300 transition-colors mb-8">
        <ArrowLeft className="w-4 h-4" /> Oltre le Carte
      </Link>
      <p className="text-xs text-amber-400/70 tracking-wide flex items-center gap-2">
        <Feather className="w-3.5 h-3.5" /> {fmtPubDate(post.published_at)}
      </p>
      <h1 className="font-display text-3xl sm:text-5xl gold-text mt-4 leading-tight" data-testid="oltre-post-title">{post.title}</h1>
      {post.image_url && (
        <img src={mediaUrl(post.image_url)} alt={post.title} className="w-full rounded-2xl border border-[#4a2175] mt-8 object-cover max-h-[420px]" data-testid="oltre-post-image" />
      )}
      <div className="mystic-card mt-8 p-7 sm:p-10">
        <p className="text-purple-100/85 leading-loose whitespace-pre-line" data-testid="oltre-post-text">{post.text}</p>

        {media.length > 0 && (
          <div className="mt-8 space-y-6" data-testid="oltre-post-media">
            {media.map((m, i) =>
              m.type === "video" ? (
                ytId(m.url) ? (
                  <div key={i} className="aspect-video rounded-2xl overflow-hidden border border-[#4a2175]">
                    <iframe
                      src={`https://www.youtube.com/embed/${ytId(m.url)}`}
                      title={`${post.title} — video ${i + 1}`}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      data-testid={`oltre-post-video-${i}`}
                    />
                  </div>
                ) : (
                  <video key={i} controls src={mediaUrl(m.url)} className="w-full rounded-2xl border border-[#4a2175]" data-testid={`oltre-post-video-${i}`} />
                )
              ) : (
                <img key={i} src={mediaUrl(m.url)} alt={`${post.title} — foto ${i + 1}`} className="w-full rounded-2xl border border-[#4a2175] object-cover" loading="lazy" data-testid={`oltre-post-photo-${i}`} />
              )
            )}
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-amber-500/10 flex items-center justify-center">
          <button
            onClick={like}
            disabled={liked}
            data-testid="oltre-like-btn"
            className={`inline-flex items-center gap-2.5 rounded-full border px-7 py-3 text-sm tracking-wide transition-all duration-300 ${
              liked
                ? "border-amber-400/60 bg-amber-500/10 text-amber-200 cursor-default"
                : "border-amber-500/40 text-amber-200 hover:bg-amber-500/10 hover:border-amber-400/60"
            }`}
          >
            <Heart className={`w-4.5 h-4.5 w-[18px] h-[18px] transition-transform ${liked ? "fill-amber-400 text-amber-400 scale-110" : ""}`} />
            {liked ? "Ti è piaciuto" : "Mi piace"}
            <span className="text-purple-300/60" data-testid="oltre-like-count">· {likes}</span>
          </button>
        </div>
      </div>
    </article>
  );
}
